/**
 * Normalize API / network failures into short, user-facing messages.
 * Never surface stack traces, Mongo dumps, axios status boilerplate, or HTML.
 */

const STATUS_DEFAULTS = {
    400: 'Please check your input and try again.',
    401: 'Please sign in again to continue.',
    403: 'You do not have permission to do this.',
    404: 'The requested item was not found.',
    408: 'The request timed out. Please try again.',
    409: 'This conflicts with an existing record.',
    413: 'The upload is too large. Please use a smaller file.',
    422: 'Some of the information provided is invalid.',
    429: 'Too many requests. Please wait a moment and try again.',
    500: 'Something went wrong on our side. Please try again.',
    502: 'The service is temporarily unavailable. Please try again.',
    503: 'The service is temporarily unavailable. Please try again.',
    504: 'The request timed out. Please try again.',
};

/** Messages that are axios/browser boilerplate — never show to users. */
const TECHNICAL_MESSAGE_RE = /Cast to ObjectId|E11000|MongoServerError|MongoError|ValidationError|bulk write|at\s+\S+\s+\(|TypeError:|ReferenceError:|SyntaxError:|Cannot read prop|Cannot set prop|ECONNREFUSED|ENOTFOUND|ECONNRESET|mongoose\.|bson|Unexpected token|Request failed with status code|Network Error|ENOENT|EACCES|is not valid JSON|API returned HTML|Failed to fetch|ERR_/i;

export function isHtmlResponseError(err) {
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trimStart().startsWith('<')) return true;
    const msg = String(err?.message || '');
    return /Unexpected token\s*'<'/i.test(msg) || /is not valid JSON/i.test(msg) || /API returned HTML/i.test(msg);
}

function looksTechnical(message) {
    const m = String(message || '').trim();
    if (!m) return true;
    if (m.length > 280) return true;
    return TECHNICAL_MESSAGE_RE.test(m);
}

function firstValidationMsg(data) {
    const list = data?.errors;
    if (!Array.isArray(list) || !list.length) return null;
    const parts = list
        .map((e) => e?.msg || e?.message || (typeof e === 'string' ? e : null))
        .filter(Boolean);
    if (!parts.length) return null;
    return parts.slice(0, 3).join('. ');
}

function pickRawMessage(err) {
    const data = err?.response?.data;
    if (data && typeof data === 'object') {
        if (typeof data.error === 'string' && data.error.trim()) return data.error.trim();
        const fromErrors = firstValidationMsg(data);
        if (fromErrors) return fromErrors;
        if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();
    }
    if (typeof data === 'string' && data.trim() && !data.trimStart().startsWith('<')) {
        return data.trim().slice(0, 240);
    }
    return null;
}

function statusFallback(status, fallback) {
    if (status && STATUS_DEFAULTS[status]) return STATUS_DEFAULTS[status];
    if (status >= 500) return STATUS_DEFAULTS[500];
    return fallback;
}

/**
 * @param {unknown} err - axios / Error
 * @param {string} [fallback] - contextual fallback when no useful API message exists
 * @returns {string}
 */
export function formatApiError(err, fallback = 'Something went wrong. Please try again.') {
    if (err?.userMessage && typeof err.userMessage === 'string' && !looksTechnical(err.userMessage)) {
        return err.userMessage;
    }

    const status = err?.response?.status;

    if (isHtmlResponseError(err)) {
        return 'Could not reach the server. Please check your connection and try again.';
    }
    if (err?.code === 'ECONNABORTED' || status === 408 || status === 504) {
        return 'The request timed out. Please try again.';
    }
    if (!err?.response && (/Network Error/i.test(String(err?.message || '')) || err?.code === 'ERR_NETWORK')) {
        return 'Network error. Check your internet connection and try again.';
    }

    // Special app codes with dedicated copy
    const code = err?.response?.data?.code;
    if (code === 'WORKDAY_GATE') {
        return err?.response?.data?.error || 'Please complete your workday check-in first.';
    }
    if (code === 'CONTACT_ADMIN') {
        return err?.response?.data?.error || 'Please contact your administrator for help.';
    }
    if (code === 'PENDING_PRIOR_CLOSING') {
        return err?.response?.data?.error || 'A previous day closing is still pending. Finish that first.';
    }
    if (code === 'DUPLICATE') {
        return pickRawMessage(err) || 'A record with these details already exists.';
    }

    const raw = pickRawMessage(err);
    if (raw && !looksTechnical(raw)) {
        return raw;
    }

    // Permission / access
    if (status === 403) {
        if (raw && !looksTechnical(raw) && !/^Access denied:\s*[\w.]+\s+not allowed/i.test(raw)) {
            return raw;
        }
        return STATUS_DEFAULTS[403];
    }

    return statusFallback(status, fallback);
}

/** Alias used by HR modules and newer call sites. */
export function getApiError(err, fallback) {
    return formatApiError(err, fallback);
}

export function getApiErrorMessage(err, fallback) {
    return formatApiError(err, fallback);
}

/**
 * Soften known permission strings from the API before display.
 * Used by the axios interceptor so existing `data.error || 'Failed…'` call sites improve automatically.
 */
export function sanitizeApiErrorPayload(data, status) {
    if (!data || typeof data !== 'object') return data;
    const next = { ...data };
    const synthetic = { response: { data: next, status } };
    const friendly = formatApiError(synthetic, statusFallback(status, 'Something went wrong. Please try again.'));
    next.error = friendly;
    if (typeof next.message === 'string' && (looksTechnical(next.message) || next.message === data.error)) {
        next.message = friendly;
    }
    return next;
}

export { STATUS_DEFAULTS, looksTechnical as isTechnicalErrorMessage };
